import React, { useState } from 'react';
import { styled } from '@mui/material';
import {
    RFieldGroup,
    RPasswordInput,
    RPillButton,
    RErrorText,
    RRow,
    RCaption,
    RCheckbox,
    RLink,
} from 'recruit/components';
import { recruitComplete } from 'recruit/api/candidate_auth';
import { setRecruitToken, useRecruitCandidateUser } from 'recruit/context/RecruitCandidateUserContext';
import { PrivacyPolicyUrl, ProviderTermsOfUseUrl } from 'constants/Urls';

interface CandidateSetPasswordFormProps {
    onComplete: () => void;
}

export function CandidateSetPasswordForm({ onComplete }: CandidateSetPasswordFormProps) {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [consentAgreement, setConsentAgreement] = useState(false);
    const { pendingSignup, setPendingSignup, verifiedVia, refreshCandidateUser } = useRecruitCandidateUser();

    async function handleSubmit() {
        setError('');

        if (!password || !confirmPassword) {
            setError('Please fill in both fields.');
            return;
        }

        if (!Object.is(password, confirmPassword)) {
            setError('Passwords do not match.');
            return;
        }

        if (password.length < 8) {
            setError('Password must be at least 8 characters.');
            return;
        }

        setLoading(true);
        try {
            const res = await recruitComplete({
                ...pendingSignup!,
                password,
                verified_via: verifiedVia!,
            });
            setRecruitToken(res.key);
            setPendingSignup(undefined);
            await refreshCandidateUser();
            onComplete();
        } catch {
            setError('Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    }

    return (
        <FieldStack>
            <RFieldGroup label="Password">
                <RPasswordInput value={password} onChange={setPassword} placeholder="Enter a password" fullWidth />
            </RFieldGroup>
            <RFieldGroup label="Confirm Password">
                <RPasswordInput
                    value={confirmPassword}
                    onChange={setConfirmPassword}
                    placeholder="Confirm your password"
                    fullWidth
                />
            </RFieldGroup>
            <RRow gap="sm">
                <RCheckbox checked={consentAgreement} onChange={setConsentAgreement} size="small" />
                <RCaption>
                    By applying, you agree to our{' '}
                    <RLink href={ProviderTermsOfUseUrl}>Terms of Use</RLink> and{' '}
                    <RLink href={PrivacyPolicyUrl}>Privacy Policy</RLink>.
                </RCaption>
            </RRow>
            {error && <RErrorText>{error}</RErrorText>}
            <RPillButton onClick={handleSubmit} disabled={loading}>
                {loading ? 'Creating account...' : 'Create Account'}
            </RPillButton>
        </FieldStack>
    );
}

const FieldStack = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
});
